import axios from 'axios';
import { Task, TaskStage } from '../types/Task';
import { format } from 'date-fns';
import { ServiceArea } from '../types/ForemanArea';
import { generatePSTTimestampString } from '../utils/dateUtils';

interface D3ReportOptions {
  serviceArea: string;
  year: number;
  month: number;
  preliminary?: boolean;
  statuses?: string[];
  startDate?: string;
  endDate?: string;
  postedDate?: string;
  customFilename?: string;
}

export interface FinanceMonthEndCell {
  qty: number;
  value: number;
}

export interface FinanceMonthEndRow {
  rowType: 'summary' | 'foremanArea' | 'grandTotal';
  activityNumber: string;
  activityName: string;
  foremanArea: string;
  foremanAreaId: number | null;
  job: string;
  unitOfMeasure: string;
  months: FinanceMonthEndCell[];
  totalQty: number;
  totalValue: number;
}

export interface FinanceMonthEndResponse {
  monthHeaders: Array<{ label: string; dateRange: string }>;
  rows: FinanceMonthEndRow[];
  diagnostics: {
    tasksSkippedNoDate: number;
    tasksSkippedOutsideMonths: number;
    tasksWithoutForemanArea: number;
  };
}

export interface ReportHistory {
  id: number;
  name: string;
  type: string;
  exportType?: string;
  serviceAreaId: number;
  serviceArea: {
    id: number;
    name: string;
    code: string;
  };
  generatedById: number;
  generatedBy: {
    name: string;
    lastName: string;
  };
  createdAt: string | null; // Allow null for missing dates
  fileURL: string;
}

// Interface for Wildlife Report Preview Data
export interface WildlifePreviewData {
  id: number;
  formIdEmployee: string;
  employeeName?: string; // Add employee name field
  recordType: string;
  serviceArea: string;
  accidentDate: string;
  timeOfKill: string;
  latitude: string;
  longitude: string;
  highwayUnique: string;
  landmark: string;
  offset: string;
  nearestTown: string;
  wildlifeSign: string;
  quantity: string;
  species: string;
  sex: string;
  age: string;
  comments: string;
}

// Interface for Rockfall Report Preview Data
export interface RockfallPreviewData {
  id: number;
  formIdEmployee: string;
  recordType: string;
  serviceArea: string;
  mcrrIncidentNumber: string;
  estimatedRockfallDate: string;
  estimatedRockfallTime: string;
  startLatitude: string;
  startLongitude: string;
  endLatitude: string;
  endLongitude: string;
  highwayUnique: string;
  highwayUniqueName: string;
  landmark: string;
  landmarkName: string;
  startOffset: string;
  endOffset: string;
  directionFromLandmark: string;
  locationDescription: string;
  ditchVolume: string;
  travelledLanesVolume: string;
  otherDitchVolume: string;
  otherTravelledLanesVolume: string;
  heavyPrecipitation: string;
  freezeThaw: string;
  ditchSnowIce: string;
  vehicleDamage: string;
  comments: string;
  name: string; // This is the employee name for Rockfall
  mcPhoneNumber: string;
  nameOfMaintenanceContractor: string;
  reportDate: string;
}

// Interface for Signs Report Preview Data
export interface SignsPreviewData {
  id: number;
  formIdEmployee: string;
  employeeName?: string; // Add employee name field
  inventoryType: string;
  rfiRoute: string;
  startChainage: string;
  endChainage: string;
  adminUnit: string;
  startDate: string;
  xps: string;
  catalogNumber: string;
  directionFacing: string;
  signPostType: string;
  numberOfPosts: string;
  largeSignFlag: string;
  districtSignNumber: string;
  installDate: string;
  comments: string;
  xCoord: string;
  yCoord: string;
}

// Interface for Culverts Report Preview Data
export interface CulvertsPreviewData {
  id: number;
  formIdEmployee: string;
  employeeName?: string; // Add employee name field
  inventoryType: string;
  rfiRoute: string;
  startChainage: string;
  endChainage: string;
  adminUnit: string;
  startDate: string;
  xps: string;
  culvertType: string;
  culvertDiameter: string;
  culvertMaterial: string;
  confinedSpace: string;
  installationDate: string;
  fishSensitive: string;
  fishPassage: string;
  grate: string;
  flapGrate: string;
  comments: string;
  xCoord: string;
  yCoord: string;
}

// Interface for Cattleguard Report Preview Data
export interface CattleguardPreviewData {
  id: number;
  formIdEmployee: string;
  employeeName?: string; // Add employee name field
  inventoryType: string;
  rfiRoute: string;
  startChainage: string;
  endChainage: string;
  adminUnit: string;
  startDate: string;
  xps: string;
  cattleGuardType: string;
  cattleGuardWidth: string;
  cattleGuardLength: string;
  runningStrips: string;
  cattleGuardBase: string;
  installationDate: string;
  fishSensitive: string;
  fishPassage: string;
  grate: string;
  flapGrate: string;
  comments: string;
  xCoord: string;
  yCoord: string;
}

// Interface for Equipment Usage Report Preview Data
export interface EquipmentUsagePreviewData {
  id: string;
  date: string; // mm/dd/yyyy format
  employeeCode: string; // first 2 chars + 3 chars after space
  yardActivityCode: string; // foreman area + project/task logic
  activityNumber: string; // formatted activity number
  vehicleUsed: string; // vehicle used (primary or secondary)
  vehicleHours: number; // vehicle hours
  vehicleRate: number; // vehicle rate
  financeClassification: string; // Display labels from project/task; Sage export maps to EQ1–EQ5
}

export interface MaterialUsagePreviewData {
  materialType?: string;
  id: string;
  employeeName: string;
  pickupYard: string;
  quantity: number;
  unitOfMeasure: string;
  materialUsed: string;
  taskNumber: string;
  accomplishmentAssets: string;
  projectId: string;
  materialRate: number;
  totalCost: number;
  vehicle: string;
  date: string;
  activityNumber: string;
  job: string;
  costCode: string;
  owner: string;
  siteCode: string;
}

export interface OverdueTasksPreviewData {
  id: number;
  taskNumber: string | null;
  name: string | null;
  description: string | null;
  status: string;
  plannedCompletionDate: string | null;
  startDate: string | null;
  identifiedDate: string | null;
  completionDate: string | null;
  type: string;
  priority: string | null;
  projectName: string | null;
  projectNumber: string | null;
  foremanArea: string | null;
  serviceArea: string | null;
  activityNumber: string | null;
  estimatedEffortHours: number | null;
  comments: string | null;
  daysOverdue: number | null;
}

export interface OperationsLabourPreviewData {
  id: string;
  formName: string;
  formDate: string | null;
  submittedAt: string;
  employeeName: string | null;
  status: string;
  serviceArea: string | null;
  foremanArea: string | null;
  submittedLabourHours: number;
  approvedLabourHours: number;
  overtimeHours: number;
  hasOvertime: boolean;
}

class ReportService {
  /**
   * Generates a D3 report for closed quantified tasks
   * @param options Report options including service area and date
   * @returns CSV content as a string
   */
  async generateD3Report(options: D3ReportOptions): Promise<string> {
    try {
      // Fetch all closed quantified tasks
      const response = await axios.get('/api/tasks/quantified', {
        params: {
          status: TaskStage.CLOSED,
          // Additional filters can be added here
        }
      });
      
      const tasks: Task[] = response.data;
      
      // Generate CSV content
      let csvContent = "RECORD TYPE,SERVICE AREA,RECORD NUMBER,TASK NUMBER,ACTIVITY NUMBER,START DATE,END DATE,ACCOMPLISHMENT,UNIT OF MEASURE,POSTED DATE,HIGHWAY UNIQUE,START LATITUDE,START LONGITUDE,END LATITUDE,END LONGITUDE,STRUCTURE NUMBER,SITE NUMBER,VALUE OF WORK,COMMENTS\n";
      
      // Process each task
      tasks.forEach(task => {
        // Skip tasks that don't match the service area if specified
        if (options.serviceArea && task.foremanArea?.serviceArea?.name !== options.serviceArea) {
          return;
        }
        
        // Get task locations
        const startLocation = task.taskLocations?.find(loc => loc.type === 'START');
        const endLocation = task.taskLocations?.find(loc => loc.type === 'END');
        
        // Get quantified task details
        const quantifiedTask = task.quantifiedTasks?.[0]; // Assuming one quantified task per task
        
        // Get task costing
        const taskCosting = task.taskCostings?.[0]; // Assuming one costing per task
        
        // Format dates according to requirements (YYYY-MM-DD)
        const identifiedDate = task.identifiedDate ? format(new Date(task.identifiedDate), 'yyyy-MM-dd') : '';
        const completionDate = task.completionDate ? format(new Date(task.completionDate), 'yyyy-MM-dd') : '';
        const postedDate = task.updatedAt   ? format(new Date(task.updatedAt), 'yyyy-MM-dd') : '';
        
        // Determine record type (R for routine, Q for quantified)
        const recordType = 'Q'; // Since we're only dealing with quantified tasks
        
        // Get activity number from the first activity if available
        const activityNumber = task.activities && task.activities.length > 0 && task.activities[0].activityNumber 
          ? task.activities[0].activityNumber 
          : '';
        
        // Create CSV row
        const row = [
          recordType,
          task.foremanArea?.serviceArea || '',
          task.taskNumber ? task.taskNumber.substring(1) : task.id.toString(),
          task.id,
          activityNumber,
          '',
          completionDate,
          task.completionAccomplishment || quantifiedTask?.identifiedQuantity || '',
          quantifiedTask?.unitOfMeasure || '',
          postedDate,
          task.taskLocations?.[0]?.asset?.highwayUnique || '', // Highway Unique
          startLocation?.latitude || '',
          startLocation?.longitude || '',
          endLocation?.latitude || '',
          endLocation?.longitude || '',
          (task.taskLocations?.[0]?.asset?.assetType && 
           !['ROAD', 'SIGN', 'CULVERT', 'CATTLEGUARD'].includes(task.taskLocations[0].asset.assetType)) 
            ? task.taskLocations[0].asset.assetNumber || '' 
            : '',
          taskCosting?.totalValue || '',
          task.comments || ''
        ].join(',');
        
        csvContent += row + '\n';
        
        // Update task status to "Finalized" (if needed)
        this.updateTaskToFinalized(task.id);
      });
      
      return csvContent;
    } catch (error) {
      console.error('Error generating D3 report:', error);
      throw error;
    }
  }
  
  /**
   * Updates a task status to "Finalized"
   * @param taskId The ID of the task to update
   */
  private async updateTaskToFinalized(taskId: number): Promise<void> {
    try {
      await axios.patch(`/api/tasks/${taskId}/status`, {
        status: 'FINALIZED',
        comments: 'Task finalized after being included in D3 report'
      });
    } catch (error) {
      console.error(`Error updating task ${taskId} to Finalized:`, error);
    }
  }
  
  /**
   * Downloads a D3 report as a CSV file
   * @param options Report options
   */
  async downloadD3Report(options: D3ReportOptions): Promise<void> {
    try {
      // Ensure service area is a valid ID
      const serviceAreaId = parseInt(options.serviceArea);
      if (isNaN(serviceAreaId)) {
        console.error('Invalid service area ID:', options.serviceArea);
        throw new Error('Invalid service area ID');
      }
      
      // Use custom filename if provided, otherwise generate default filename
      let filename;
      if (options.customFilename) {
        filename = options.customFilename.endsWith('.csv') ? options.customFilename : `${options.customFilename}.csv`;
      } else {
        const serviceAreaCode = options.serviceArea.replace(/\D/g, '') || '00';
        const yearMonth = `${options.year}${options.month.toString().padStart(2, '0')}`;
        filename = `SA${serviceAreaCode}_D3_${yearMonth}.csv`;
      }
      
                             // Call the server API to get the report
         const response = await axios.get('/api/reports/d3/export', {
          params: {
            serviceArea: serviceAreaId, // Pass the numeric ID
            year: options.year,
            month: options.month,
            preliminary: options.preliminary, // Add preliminary parameter
            statuses: options.statuses && options.statuses.length > 0 ? options.statuses.join(',') : undefined, // Convert array to comma-separated string
            startDate: options.startDate, // Add start date filter
            endDate: options.endDate, // Add end date filter
            postedDate: options.postedDate, // Add posted date for final exports
            customFilename: options.customFilename // Add custom filename
          },
          responseType: 'blob' // Important for file download
        });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading D3 report:', error);
      throw error;
    }
  }

  /**
   * Downloads a D2 report as a CSV file
   * @param options Report options including service area and date
   */
  async downloadD2Report(options: D3ReportOptions): Promise<void> {
    try {
      // Ensure service area is a valid ID
      const serviceAreaId = parseInt(options.serviceArea);
      if (isNaN(serviceAreaId)) {
        console.error('Invalid service area ID:', options.serviceArea);
        throw new Error('Invalid service area ID');
      }
      
      // Use custom filename if provided, otherwise generate default filename
      let filename;
      if (options.customFilename) {
        filename = options.customFilename.endsWith('.csv') ? options.customFilename : `${options.customFilename}.csv`;
      } else {
        const serviceAreaCode = options.serviceArea.replace(/\D/g, '') || '00';
        const yearMonth = `${options.year}${options.month.toString().padStart(2, '0')}`;
        filename = `SA${serviceAreaCode.padStart(2, '0')}_D2_${yearMonth}.csv`;
      }
      
                             // Call the server API to get the report
         const response = await axios.get('/api/reports/d2/export', {
          params: {
            serviceArea: serviceAreaId, // Pass the numeric ID
            year: options.year,
            month: options.month,
            preliminary: options.preliminary, // Add preliminary parameter
            statuses: options.statuses && options.statuses.length > 0 ? options.statuses.join(',') : undefined, // Convert array to comma-separated string
            startDate: options.startDate, // Add start date filter
            endDate: options.endDate, // Add end date filter
            postedDate: options.postedDate, // Add posted date for final exports
            customFilename: options.customFilename // Add custom filename
          },
          responseType: 'blob' // Important for file download
        });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading D2 report:', error);
      throw error;
    }
  }

  /**
   * Downloads a Finance report as a CSV file
   * @param options Report options including service area and date
   */
  async downloadFinanceReport(options: D3ReportOptions): Promise<void> {
    try {
      // Ensure service area is a valid ID
      const serviceAreaId = parseInt(options.serviceArea);
      if (isNaN(serviceAreaId)) {
        console.error('Invalid service area ID:', options.serviceArea);
        throw new Error('Invalid service area ID');
      }
      
      // Call the server API to get the report
      const response = await axios.get('/api/reports/finance', {
        params: {
          serviceArea: serviceAreaId,
          year: options.year,
          month: options.month
        },
        responseType: 'blob' // Important for file download
      });
      
      // Format filename: Finance_YYYYMM.csv
      const yearMonth = `${options.year}${options.month.toString().padStart(2, '0')}`;
      const filename = `Finance_${yearMonth}.csv`;
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Finance report:', error);
      throw error;
    }
  }

  /**
   * Gets all service areas for filtering
   * @returns List of service areas
   */
  async getServiceAreas(): Promise<any[]> {
    try {
      const response = await axios.get('/api/areas/service-areas');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching service areas:', error);
      return [];
    }
  }

  /**
   * Gets the report history
   * @param filters Optional filters for report type, service area, export type, and date range
   * @param page Page number for pagination
   * @param limit Number of items per page
   * @returns Paginated list of report history items
   */
  async getReportHistory(
    filters: {
      type?: string;
      serviceArea?: string;
      exportType?: string;
      startDate?: string;
      endDate?: string;
    } = {},
    page: number = 1,
    limit: number = 50
  ): Promise<{data: ReportHistory[], pagination: {page: number, limit: number, total: number, totalPages: number}}> {
    try {
      const params: any = {
        page: page.toString(),
        limit: limit.toString()
      };
      
      if (filters.type) {
        params.type = filters.type;
      }
      if (filters.serviceArea) {
        params.serviceArea = filters.serviceArea;
      }
      if (filters.exportType) {
        params.exportType = filters.exportType;
      }
      if (filters.startDate) {
        params.startDate = filters.startDate;
      }
      if (filters.endDate) {
        params.endDate = filters.endDate;
      }
      
      const response = await axios.get('/api/reports/history', { params });
      
      // Ensure we always return a valid response structure
      if (!response.data) {
        console.warn('No data returned from report history API');
        return {
          data: [],
          pagination: { page, limit, total: 0, totalPages: 0 }
        };
      }
      
      // Handle both old format (array) and new format (object with data and pagination)
      if (Array.isArray(response.data)) {
        // Legacy format - convert to new format
        return {
          data: response.data,
          pagination: { page, limit, total: response.data.length, totalPages: 1 }
        };
      }
      
      if (response.data.data && response.data.pagination) {
        return response.data;
      }
      
      console.warn('Unexpected response format from report history API:', response.data);
      return {
        data: [],
        pagination: { page, limit, total: 0, totalPages: 0 }
      };
    } catch (error) {
      console.error('Error fetching report history:', error);
      // Return empty paginated response on error
      return {
        data: [],
        pagination: { page, limit, total: 0, totalPages: 0 }
      };
    }
  }

  /**
   * Downloads a report by ID
   * @param reportId The ID of the report to download
   */
  async downloadReportById(reportId: number): Promise<void> {
    try {
      if (!reportId || isNaN(reportId)) {
        console.error('Invalid report ID:', reportId);
        throw new Error('Invalid report ID');
      }
      
      window.open(`/api/reports/download/${reportId}`, '_blank');
    } catch (error) {
      console.error('Error downloading report:', error);
      throw error;
    }
  }

  /**
   * Gets D3 report preview data without changing task status
   * @param options Preview options
   * @returns Preview data for D3 report
   */
  async getD3PreviewData(options: {
    serviceArea?: string;
    statuses?: string[];
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<{data: any[], pagination: {page: number, limit: number, total: number, totalPages: number}}> {
    try {
      const params: any = {};
      
      if (options.serviceArea) {
        params.serviceArea = options.serviceArea;
      }
      
      if (options.statuses && options.statuses.length > 0) {
        params.statuses = options.statuses.join(',');
      }
      
      if (options.startDate) {
        params.startDate = options.startDate;
      }
      
      if (options.endDate) {
        params.endDate = options.endDate;
      }
      
      if (options.page) {
        params.page = options.page;
      }
      
      if (options.limit) {
        params.limit = options.limit;
      }

      if (options.sortField) {
        params.sortField = options.sortField;
      }

      if (options.sortDirection) {
        params.sortDirection = options.sortDirection;
      }
      
      const response = await axios.get('/api/reports/d3/preview', { params });
      
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      console.error('Error fetching D3 preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  /**
   * Gets D2 report preview data without changing task status
   * @param options Preview options
   * @returns Preview data for D2 report
   */
  async getD2PreviewData(options: {
    serviceArea?: string;
    statuses?: string[];
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<{data: any[], pagination: {page: number, limit: number, total: number, totalPages: number}}> {
    try {
      const params: any = {};
      
      if (options.serviceArea) {
        params.serviceArea = options.serviceArea;
      }
      
      if (options.statuses && options.statuses.length > 0) {
        params.statuses = options.statuses.join(',');
      }
      
      if (options.startDate) {
        params.startDate = options.startDate;
      }
      
      if (options.endDate) {
        params.endDate = options.endDate;
      }
      
      if (options.page) {
        params.page = options.page;
      }
      
      if (options.limit) {
        params.limit = options.limit;
      }

      if (options.sortField) {
        params.sortField = options.sortField;
      }

      if (options.sortDirection) {
        params.sortDirection = options.sortDirection;
      }
      
      const response = await axios.get('/api/reports/d2/preview', { params });
      
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      console.error('Error fetching D2 preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  // Wildlife Report Methods
  async getWildlifePreviewData(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<{
    data: WildlifePreviewData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await axios.get('/api/reports/wildlife/preview', {
        params: filters
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      console.error('Error fetching wildlife preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async generateWildlifeReport(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      // Call the server API to get the report
      const response = await axios.get('/api/reports/wildlife/export', {
        params: {
          ...filters,
          customFilename: filters.customFilename // Pass the custom filename to the server
        },
        responseType: 'blob' // Important for file download
      });
      
      let filename = 'wildlife_report.csv';
      
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        // Default format: wildlife_report_YYYY_MM_DD_HHMM
        const timestamp = generatePSTTimestampString();
        filename = `wildlife_report_${timestamp}.csv`;
      }
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading wildlife report:', error);
      throw error;
    }
  }

  // Rockfall Report Methods
  async getRockfallPreviewData(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<{
    data: RockfallPreviewData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await axios.get('/api/reports/rockfall/preview', {
        params: filters
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      console.error('Error fetching rockfall preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async generateRockfallReport(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      // Call the server API to get the report
      const response = await axios.get('/api/reports/rockfall/export', {
        params: {
          ...filters,
          customFilename: filters.customFilename // Pass the custom filename to the server
        },
        responseType: 'blob' // Important for file download
      });
      
      let filename = 'rockfall_report.csv';
      
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        // Default format: rockfall_report_YYYY_MM_DD_HHMM
        const timestamp = generatePSTTimestampString();
        filename = `rockfall_report_${timestamp}.csv`;
      }
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading rockfall report:', error);
      throw error;
    }
  }

  // Signs Report Methods
  async getSignsPreviewData(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<{
    data: SignsPreviewData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await axios.get('/api/reports/signs/preview', {
        params: filters
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      console.error('Error fetching signs preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async generateSignsReport(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      // Call the server API to get the report
      const response = await axios.get('/api/reports/signs/export', {
        params: filters,
        responseType: 'blob' // Important for file download
      });
      
      let filename = 'signs_report.csv';
      
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        // Default format: signs_report_YYYY_MM_DD_HHMM
        const timestamp = generatePSTTimestampString();
        filename = `signs_report_${timestamp}.csv`;
      }
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading signs report:', error);
      throw error;
    }
  }

  // Culverts Report Methods
  async getCulvertsPreviewData(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<{
    data: CulvertsPreviewData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await axios.get('/api/reports/culverts/preview', {
        params: filters
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      console.error('Error fetching culverts preview data:', error);
      throw error;
    }
  }

  async generateCulvertsReport(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      // Call the server API to get the report
      const response = await axios.get('/api/reports/culverts/export', {
        params: filters,
        responseType: 'blob' // Important for file download
      });
      
      let filename = 'culverts_report.csv';
      
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        // Default format: culverts_report_YYYY_MM_DD_HHMM
        const timestamp = generatePSTTimestampString();
        filename = `culverts_report_${timestamp}.csv`;
      }
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading culverts report:', error);
      throw error;
    }
  }

  // Cattleguard Report Methods
  async getCattleguardPreviewData(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<{
    data: CattleguardPreviewData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await axios.get('/api/reports/cattleguard/preview', {
        params: filters
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      console.error('Error fetching cattleguard preview data:', error);
      throw error;
    }
  }

  async generateCattleguardReport(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      // Call the server API to get the report
      const response = await axios.get('/api/reports/cattleguard/export', {
        params: filters,
        responseType: 'blob' // Important for file download
      });
      
      let filename = 'cattleguard_report.csv';
      
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        // Default format: cattleguard_report_YYYY_MM_DD_HHMM
        const timestamp = generatePSTTimestampString();
        filename = `cattleguard_report_${timestamp}.csv`;
      }
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading cattleguard report:', error);
      throw error;
    }
  }

  // Equipment Usage Report Methods (returns all data for client-side pagination)
  async getEquipmentUsagePreviewData(
    filters: {
      serviceArea?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
      sortField?: string;
      sortDirection?: 'asc' | 'desc';
    },
    signal?: AbortSignal
  ): Promise<{
    data: EquipmentUsagePreviewData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await axios.get('/api/reports/equipment-usage/preview', {
        params: {
          ...filters,
          page: filters.page || 1,
          limit: filters.limit || 50,
          sortField: filters.sortField,
          sortDirection: filters.sortDirection
        },
        signal
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching equipment usage preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async generateEquipmentUsageReport(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    customFilename?: string;
    reportType?: string;
  }): Promise<void> {
    try {
      // Call the server API to get the report
      const response = await axios.get('/api/reports/equipment-usage/export', {
        params: {
          ...filters,
          customFilename: filters.customFilename // Pass the custom filename to the server
        },
        responseType: 'blob' // Important for file download
      });
      
      let filename = 'equipment_usage.csv';
      
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        // Default format: Equipment Usage_YYYY_MM_DD
        if (filters.endDate) {
          const date = new Date(filters.endDate);
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          filename = `Equipment Usage_${year}_${month}_${day}.csv`;
        } else {
          // Fallback to current date
          const date = new Date();
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          filename = `Equipment Usage_${year}_${month}_${day}.csv`;
        }
      }
      
      // Create a download link
      let url: string | null = null;
      try {
        url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading equipment usage report:', error);
        throw error;
      } finally {
        if (url) {
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error downloading equipment usage report:', error);
      throw error;
    }
  }

  // Material Usage Report Filter Options
  async getMaterialUsageFilterOptions(
    filters: {
      serviceArea?: string;
      startDate?: string;
      endDate?: string;
      dateFilterType?: 'submission' | 'received';
    },
    signal?: AbortSignal
  ): Promise<{
    materialUsed: string[];
    taskNumber: string[];
    projectId: string[];
    pickupYard: string[];
    accomplishmentAssets: string[];
    materialType: string[];
  }> {
    try {
      const response = await axios.get('/api/reports/material-usage/filter-options', {
        params: filters,
        signal
      });
      return response.data || {
        materialUsed: [],
        taskNumber: [],
        projectId: [],
        pickupYard: [],
        accomplishmentAssets: [],
        materialType: []
      };
    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching material usage filter options:', error);
      return {
        materialUsed: [],
        taskNumber: [],
        projectId: [],
        pickupYard: [],
        accomplishmentAssets: [],
        materialType: []
      };
    }
  }

  // Material Usage Report Methods (returns paginated data for server-side pagination)
  async getMaterialUsagePreviewData(
    filters: {
      serviceArea?: string;
      startDate?: string;
      endDate?: string;
      dateFilterType?: 'submission' | 'received';
      location?: string;
      material?: string;
      taskNumber?: string;
      project?: string;
      materialType?: string;
      pickupLocation?: string;
      page?: number;
      limit?: number;
      sortField?: string;
      sortDirection?: 'asc' | 'desc';
    },
    signal?: AbortSignal
  ): Promise<{
    data: MaterialUsagePreviewData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await axios.get('/api/reports/material-usage/preview', {
        params: {
          ...filters,
          page: filters.page || 1,
          limit: filters.limit || 50,
          sortField: filters.sortField,
          sortDirection: filters.sortDirection
        },
        signal
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching material usage preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async generateMaterialUsageReport(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    dateFilterType?: 'submission' | 'received';
    location?: string;
    material?: string;
    taskNumber?: string;
    project?: string;
    materialType?: string;
    pickupLocation?: string;
    customFilename?: string;
    reportType?: string;
  }): Promise<void> {
    try {
      // Call the server API to get the report
      const response = await axios.get('/api/reports/material-usage/export', {
        params: {
          ...filters,
          customFilename: filters.customFilename // Pass the custom filename to the server
        },
        responseType: 'blob' // Important for file download
      });
      
      let filename = 'material_usage.csv';
      
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        // Default format: Material Usage_YYYY_MM_DD
        if (filters.endDate) {
          const date = new Date(filters.endDate);
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          filename = `Material Usage_${year}_${month}_${day}.csv`;
        } else {
          // Fallback to current date
          const date = new Date();
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          filename = `Material Usage_${year}_${month}_${day}.csv`;
        }
      }
      
      // Create a download link
      let url: string | null = null;
      try {
        url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading material usage report:', error);
        throw error;
      } finally {
        if (url) {
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error downloading material usage report:', error);
      throw error;
    }
  }

  // Preliminary Job Cost Report
  async getPreliminaryJobCostPreview(
    filters: {
      serviceArea?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
      sortField?: string;
      sortDirection?: 'asc' | 'desc';
    },
    signal?: AbortSignal
  ): Promise<{ data: { job: string; activity: string; financeClass: string; date: string; description: string; amount: number; }[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const response = await axios.get('/api/reports/finance/preliminary-jobcost/preview', {
        params: {
          ...filters,
          page: filters.page || 1,
          limit: filters.limit || 50,
          sortField: filters.sortField,
          sortDirection: filters.sortDirection
        },
        signal
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching preliminary job cost preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } } as any;
    }
  }

  async generatePreliminaryJobCostReport(filters: {
    serviceArea?: string;
    startDate?: string;
    endDate?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      const response = await axios.get('/api/reports/finance/preliminary-jobcost/export', {
        params: filters,
        responseType: 'blob'
      });

      let filename = 'Preliminary_Job_Cost.csv';
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        filename = `Preliminary_Job_Cost_${generatePSTTimestampString()}.csv`;
      }

      let url: string | null = null;
      try {
        url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading preliminary job cost report:', error);
        throw error;
      } finally {
        if (url) {
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error downloading preliminary job cost report:', error);
      throw error;
    }
  }

  // QBS Summary Report
  async getQBSSummary(filters: {
    serviceAreaId: number;
    quantifiedYear: number;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    monthHeaders: Array<{ label: string; dateRange: string }>;
    annualTarget?: number;
    sections: {
      reported: {
        finalized: { values: number[] };
        subtotal: { values: number[] };
        percent: { values: number[] };
      };
      complete: {
        closed: { values: number[] };
        internalReview: { values: number[] };
        subtotal: { values: number[] };
        percent: { values: number[] };
      };
      active: {
        markedComplete: { values: number[] };
        active: { values: number[] };
        subtotal: { values: number[] };
        actioned: { values: number[] };
      };
      thePlan: {
        planned: { values: number[] };
        subtotal: { values: number[] };
        total: { values: number[] };
        estimatedPercent: { values: number[] };
        remaining: { values: number[] };
      };
      holding: {
        proposed: { values: number[] };
      };
      profitability: {
        totalValueEarned: { values: number[] };
        totalCosts: { values: number[] };
        profitMargin: { values: number[] };
        profitPercent: { values: number[] };
      };
    };
    totals: {
      expectedTotal: number;
      actualTotal: number;
      percentTotal: number;
    };
  }> {
    try {
      const response = await axios.post('/api/reports/qbs-summary', filters);
      return response.data;
    } catch (error) {
      console.error('Error fetching QBS Summary report:', error);
      throw error;
    }
  }

  // QBS Actual Details Report
  async getQBSActualDetails(filters: {
    serviceAreaId: number;
    quantifiedYear: number;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    monthHeaders: Array<{ label: string; dateRange: string }>;
    activityRows: Array<{
      activityId: number;
      activityNumber: string;
      activityName: string;
      type: string;
      monthValues: number[];
      valueToDate: number;
      costsToDate: number;
      profit: number;
      grossMarginPercent: number;
      totalAccomplishment: number;
      costPerAccomplishment: number;
      scheduled2Rate: number;
      avgActualValuePerAccomplishment: number;
      grossMarginPerAccomplishment: number;
    }>;
  }> {
    try {
      const response = await axios.post('/api/reports/qbs-actual-details', filters);
      return response.data;
    } catch (error) {
      console.error('Error fetching QBS Actual Details report:', error);
      throw error;
    }
  }

  /**
   * Downloads a QBS Summary report as a CSV file
   * @param options Report options including service area and quantified year
   */
  async downloadQBSSummary(options: {
    serviceAreaId: number;
    quantifiedYear: number;
    startDate?: string;
    endDate?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      const serviceAreaId = parseInt(options.serviceAreaId.toString());
      if (isNaN(serviceAreaId)) {
        console.error('Invalid service area ID:', options.serviceAreaId);
        throw new Error('Invalid service area ID');
      }
      
      // Use custom filename if provided, otherwise generate default filename
      let filename;
      if (options.customFilename) {
        filename = options.customFilename.endsWith('.csv') ? options.customFilename : `${options.customFilename}.csv`;
      } else {
        const serviceAreaCode = serviceAreaId.toString().replace(/\D/g, '') || '00';
        filename = `SA${serviceAreaCode.padStart(2, '0')}_QBS_Summary_Y${options.quantifiedYear}.csv`;
      }
      
      // Call the server API to get the report
      const response = await axios.get('/api/reports/qbs-summary/export', {
        params: {
          serviceAreaId: serviceAreaId,
          quantifiedYear: options.quantifiedYear,
          startDate: options.startDate,
          endDate: options.endDate,
          customFilename: options.customFilename
        },
        responseType: 'blob' // Important for file download
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading QBS Summary report:', error);
      throw error;
    }
  }

  /**
   * Downloads a QBS Actual Details report as a CSV file
   * @param options Report options including service area and quantified year
   */
  async downloadQBSActualDetails(options: {
    serviceAreaId: number;
    quantifiedYear: number;
    startDate?: string;
    endDate?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      const serviceAreaId = parseInt(options.serviceAreaId.toString());
      if (isNaN(serviceAreaId)) {
        console.error('Invalid service area ID:', options.serviceAreaId);
        throw new Error('Invalid service area ID');
      }
      
      // Use custom filename if provided, otherwise generate default filename
      let filename;
      if (options.customFilename) {
        filename = options.customFilename.endsWith('.csv') ? options.customFilename : `${options.customFilename}.csv`;
      } else {
        const serviceAreaCode = serviceAreaId.toString().replace(/\D/g, '') || '00';
        filename = `SA${serviceAreaCode.padStart(2, '0')}_QBS_ActualDetails_Y${options.quantifiedYear}.csv`;
      }
      
      // Call the server API to get the report
      const response = await axios.get('/api/reports/qbs-actual-details/export', {
        params: {
          serviceAreaId: serviceAreaId,
          quantifiedYear: options.quantifiedYear,
          startDate: options.startDate,
          endDate: options.endDate,
          customFilename: options.customFilename
        },
        responseType: 'blob' // Important for file download
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading QBS Actual Details report:', error);
      throw error;
    }
  }

  async getQBSQuantifiedValue(filters: {
    serviceAreaId: number;
    quantifiedYear: number;
    startDate?: string;
    endDate?: string;
    viewMode?: 'actual' | 'planned' | 'both';
  }): Promise<{
    monthHeaders: Array<{ label: string; dateRange: string }>;
    activityRows: Array<{
      activityId: number;
      activityNumber: string;
      activityName: string;
      type: string;
      unitOfMeasure: string;
      monthValues: Array<{ actual: number; planned: number; total: number }>;
      actualValueToDate: number;
      plannedValueToDate: number;
      totalValueToDate: number;
      totalAccomplishment: number;
      totalPlannedAccomplishment: number;
    }>;
    diagnostics: {
      tasksSkippedNoDate: number;
      tasksSkippedOutsideMonths: number;
    };
    summary: {
      annualTarget: number | null;
      totalActualValue: number;
      totalPlannedValue: number;
      totalValue: number;
      projectedPercentOfTarget: number | null;
    };
  }> {
    try {
      const response = await axios.post('/api/reports/qbs-quantified-value', filters);
      return response.data;
    } catch (error) {
      console.error('Error fetching QBS Quantified Value report:', error);
      throw error;
    }
  }

  async getQBSQuantifiedValueDrillThrough(params: {
    serviceAreaId: number;
    quantifiedYear: number;
    activityId: number;
    monthIndex: number;
    viewMode?: 'actual' | 'planned' | 'both';
    startDate?: string;
    endDate?: string;
  }): Promise<Array<{
    taskId: number;
    taskNumber: string | null;
    taskName: string | null;
    status: string;
    value: number;
    accomplishment: number | null;
    date: string | null;
  }>> {
    try {
      const response = await axios.post('/api/reports/qbs-quantified-value/drill-through', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching QBS Quantified Value drill-through tasks:', error);
      throw error;
    }
  }

  async downloadQBSQuantifiedValue(options: {
    serviceAreaId: number;
    quantifiedYear: number;
    startDate?: string;
    endDate?: string;
    viewMode?: 'actual' | 'planned' | 'both';
    customFilename?: string;
  }): Promise<void> {
    try {
      const serviceAreaId = parseInt(options.serviceAreaId.toString());
      if (isNaN(serviceAreaId)) {
        console.error('Invalid service area ID:', options.serviceAreaId);
        throw new Error('Invalid service area ID');
      }

      let filename;
      if (options.customFilename) {
        filename = options.customFilename.endsWith('.csv') ? options.customFilename : `${options.customFilename}.csv`;
      } else {
        const serviceAreaCode = serviceAreaId.toString().replace(/\D/g, '') || '00';
        filename = `SA${serviceAreaCode.padStart(2, '0')}_QBS_QuantifiedValue_Y${options.quantifiedYear}.csv`;
      }

      const response = await axios.get('/api/reports/qbs-quantified-value/export', {
        params: {
          serviceAreaId: serviceAreaId,
          quantifiedYear: options.quantifiedYear,
          startDate: options.startDate,
          endDate: options.endDate,
          viewMode: options.viewMode,
          customFilename: options.customFilename
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading QBS Quantified Value report:', error);
      throw error;
    }
  }

  async getFinanceMonthEnd(filters: {
    serviceAreaId: number;
    quantifiedYear: number;
    startDate?: string;
    endDate?: string;
  }): Promise<FinanceMonthEndResponse> {
    try {
      const response = await axios.post('/api/reports/finance/monthend', filters);
      return response.data;
    } catch (error) {
      console.error('Error fetching Finance Month-End report:', error);
      throw error;
    }
  }

  async downloadFinanceMonthEnd(options: {
    serviceAreaId: number;
    quantifiedYear: number;
    startDate?: string;
    endDate?: string;
  }): Promise<void> {
    try {
      const response = await axios.get('/api/reports/finance/monthend/export', {
        params: {
          serviceAreaId: options.serviceAreaId,
          quantifiedYear: options.quantifiedYear,
          startDate: options.startDate,
          endDate: options.endDate
        },
        responseType: 'blob'
      });

      // The server names the file from the service area code (SA16_Finance_MonthEnd_Y8.csv).
      const disposition: string = response.headers?.['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match?.[1] || `Finance_MonthEnd_Y${options.quantifiedYear}.csv`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Finance Month-End report:', error);
      throw error;
    }
  }

  async getOverdueTasksPreviewData(
    filters: {
      serviceArea?: string;
      page?: number;
      limit?: number;
      sortField?: string;
      sortDirection?: 'asc' | 'desc';
    },
    signal?: AbortSignal
  ): Promise<{
    data: OverdueTasksPreviewData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await axios.get('/api/reports/operations/overdue-tasks/preview', {
        params: {
          ...filters,
          page: filters.page || 1,
          limit: filters.limit || 50,
          sortField: filters.sortField,
          sortDirection: filters.sortDirection
        },
        signal
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching overdue tasks preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async generateOverdueTasksReport(filters: {
    serviceArea?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      // Call the server API to get the report
      const response = await axios.get('/api/reports/operations/overdue-tasks/export', {
        params: {
          ...filters,
          customFilename: filters.customFilename
        },
        responseType: 'blob' // Important for file download
      });
      
      let filename = 'overdue_tasks.csv';
      
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        // Default format: Overdue Tasks_YYYY_MM_DD
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        filename = `Overdue Tasks_${year}_${month}_${day}.csv`;
      }
      
      // Create a download link
      let url: string | null = null;
      try {
        url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        if (url) {
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error downloading overdue tasks report:', error);
      throw error;
    }
  }

  async getWeeklyPayrollPreview(options: {
    startDate?: string;
    endDate?: string;
    dateFilterType?: 'approval' | 'formDate';
    serviceArea?: string;
    employeeName?: string;
    exportStatus?: 'unexported' | 'exported' | 'all';
    page?: number;
    limit?: number;
  }): Promise<{ data: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const params: Record<string, string> = {};
      if (options.startDate) params.startDate = options.startDate;
      if (options.endDate) params.endDate = options.endDate;
      if (options.dateFilterType === 'formDate') params.dateFilterType = 'formDate';
      if (options.serviceArea) params.serviceArea = options.serviceArea;
      if (options.employeeName) params.employeeName = options.employeeName;
      params.exportStatus = options.exportStatus || 'unexported';
    if (options.page) params.page = String(options.page);
    if (options.limit) params.limit = String(options.limit);
    const response = await axios.get('/api/reports/payroll/weekly/preview', { params });
    return (
      response.data || {
        data: [],
        pagination: { page: 1, limit: 50, total: 0, totalPages: 0 }
      }
    );
    } catch (e) {
      console.error('Weekly payroll preview error', e);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async getOperationsLabourPreviewData(
    filters: {
      serviceArea?: string;
      foremanArea?: string;
      startDate?: string;
      endDate?: string;
      statuses?: string[];
      overtimeFilter?: string;
      page?: number;
      limit?: number;
      sortField?: string;
      sortDirection?: 'asc' | 'desc';
    },
    signal?: AbortSignal
  ): Promise<{
    data: OperationsLabourPreviewData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await axios.get('/api/reports/operations/labour/preview', {
        params: {
          ...filters,
          statuses: filters.statuses && filters.statuses.length > 0 ? filters.statuses.join(',') : undefined,
          page: filters.page || 1,
          limit: filters.limit || 50,
          sortField: filters.sortField,
          sortDirection: filters.sortDirection
        },
        signal
      });
      return response.data || { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching operations labour preview data:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async exportWeeklyPayrollReport(options: {
    startDate?: string;
    endDate?: string;
    dateFilterType?: 'approval' | 'formDate';
    serviceArea?: string;
    employeeName?: string;
    exportStatus?: 'unexported' | 'exported' | 'all';
    dryRun?: boolean;
  }): Promise<void> {
    const params: Record<string, string> = {};
    if (options.startDate) params.startDate = options.startDate;
    if (options.endDate) params.endDate = options.endDate;
    if (options.dateFilterType === 'formDate') params.dateFilterType = 'formDate';
    if (options.serviceArea) params.serviceArea = options.serviceArea;
    if (options.employeeName) params.employeeName = options.employeeName;
    params.exportStatus = options.exportStatus || 'unexported';
    if (options.dryRun) params.dryRun = 'true';
    const response = await axios.get('/api/reports/payroll/weekly/export', {
      params,
      responseType: 'blob'
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    let filename = 'WeeklyPayroll.csv';
    if (disposition) {
      const m = disposition.match(/filename="?([^";]+)"?/i);
      if (m) filename = m[1];
    }
    let url: string | null = null;
    try {
      url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  }

  async getWeeklyOvertimePreview(options: {
    startDate?: string;
    endDate?: string;
    dateFilterType?: 'approval' | 'formDate';
    serviceArea?: string;
    employeeName?: string;
    foremanArea?: string;
    exportStatus?: 'unexported' | 'exported' | 'all';
    page?: number;
    limit?: number;
  }): Promise<{ data: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const params: Record<string, string> = {};
      if (options.startDate) params.startDate = options.startDate;
      if (options.endDate) params.endDate = options.endDate;
      if (options.dateFilterType === 'formDate') params.dateFilterType = 'formDate';
      if (options.serviceArea) params.serviceArea = options.serviceArea;
      if (options.employeeName) params.employeeName = options.employeeName;
      if (options.foremanArea) params.foremanArea = options.foremanArea;
      params.exportStatus = options.exportStatus || 'unexported';
      if (options.page) params.page = String(options.page);
      if (options.limit) params.limit = String(options.limit);
      const response = await axios.get('/api/reports/payroll/weekly-overtime/preview', { params });
      return (
        response.data || {
          data: [],
          pagination: { page: 1, limit: 50, total: 0, totalPages: 0 }
        }
      );
    } catch (e) {
      console.error('Weekly overtime preview error', e);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async exportWeeklyOvertimeReport(options: {
    startDate?: string;
    endDate?: string;
    dateFilterType?: 'approval' | 'formDate';
    serviceArea?: string;
    employeeName?: string;
    foremanArea?: string;
    exportStatus?: 'unexported' | 'exported' | 'all';
    dryRun?: boolean;
  }): Promise<void> {
    const params: Record<string, string> = {};
    if (options.startDate) params.startDate = options.startDate;
    if (options.endDate) params.endDate = options.endDate;
    if (options.dateFilterType === 'formDate') params.dateFilterType = 'formDate';
    if (options.serviceArea) params.serviceArea = options.serviceArea;
    if (options.employeeName) params.employeeName = options.employeeName;
    if (options.foremanArea) params.foremanArea = options.foremanArea;
    params.exportStatus = options.exportStatus || 'unexported';
    if (options.dryRun) params.dryRun = 'true';
    const response = await axios.get('/api/reports/payroll/weekly-overtime/export', {
      params,
      responseType: 'blob'
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    let filename = 'WeeklyOvertime.csv';
    if (disposition) {
      const m = disposition.match(/filename="?([^";]+)"?/i);
      if (m) filename = m[1];
    }
    let url: string | null = null;
    try {
      url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  }

  async getPayrollVariancePreview(options: {
    startDate?: string;
    endDate?: string;
    serviceArea?: string;
    employeeName?: string;
    exportStatus?: 'unexported' | 'exported' | 'all';
    postPayment?: 'yes' | 'no' | 'all';
    page?: number;
    limit?: number;
  }): Promise<{ data: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const params: Record<string, string> = {};
      if (options.startDate) params.startDate = options.startDate;
      if (options.endDate) params.endDate = options.endDate;
      if (options.serviceArea) params.serviceArea = options.serviceArea;
      if (options.employeeName) params.employeeName = options.employeeName;
      params.exportStatus = options.exportStatus || 'unexported';
      params.postPayment = options.postPayment || 'yes';
      if (options.page) params.page = String(options.page);
      if (options.limit) params.limit = String(options.limit);
      const response = await axios.get('/api/reports/payroll/variance/preview', { params });
      return (
        response.data || {
          data: [],
          pagination: { page: 1, limit: 50, total: 0, totalPages: 0 }
        }
      );
    } catch (e) {
      console.error('Payroll variance preview error', e);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  async exportPayrollVarianceReport(options: {
    startDate?: string;
    endDate?: string;
    serviceArea?: string;
    employeeName?: string;
    exportStatus?: 'unexported' | 'exported' | 'all';
    postPayment?: 'yes' | 'no' | 'all';
    dryRun?: boolean;
  }): Promise<void> {
    const params: Record<string, string> = {};
    if (options.startDate) params.startDate = options.startDate;
    if (options.endDate) params.endDate = options.endDate;
    if (options.serviceArea) params.serviceArea = options.serviceArea;
    if (options.employeeName) params.employeeName = options.employeeName;
    params.exportStatus = options.exportStatus || 'unexported';
    params.postPayment = options.postPayment || 'yes';
    if (options.dryRun) params.dryRun = 'true';
    const response = await axios.get('/api/reports/payroll/variance/export', {
      params,
      responseType: 'blob'
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    let filename = 'PayrollVariance.csv';
    if (disposition) {
      const m = disposition.match(/filename="?([^";]+)"?/i);
      if (m) filename = m[1];
    }
    let url: string | null = null;
    try {
      url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  }

  async generateOperationsLabourReport(filters: {
    serviceArea?: string;
    foremanArea?: string;
    startDate?: string;
    endDate?: string;
    statuses?: string[];
    overtimeFilter?: string;
    customFilename?: string;
  }): Promise<void> {
    try {
      const response = await axios.get('/api/reports/operations/labour/export', {
        params: {
          ...filters,
          statuses: filters.statuses && filters.statuses.length > 0 ? filters.statuses.join(',') : undefined
        },
        responseType: 'blob'
      });

      let filename = 'submitted_time.csv';
      if (filters.customFilename) {
        filename = filters.customFilename.endsWith('.csv') ? filters.customFilename : `${filters.customFilename}.csv`;
      } else {
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        filename = `Submitted_Time_${year}_${month}_${day}.csv`;
      }

      let url: string | null = null;
      try {
        url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        if (url) {
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error downloading operations labour report:', error);
      throw error;
    }
  }
}

export const reportService = new ReportService();
export default reportService; 